import { useEffect, useRef, useState, type MouseEvent } from 'react';
import clsx from 'clsx';

export type ImageCellStatus = 'loading' | 'loaded' | 'failed' | 'not_extracted';

interface ImageCellProps {
  /** Extracted image URL. Absent or empty means the extractor returned nothing. */
  src?: string | null;
  alt: string;
  /** Size and shape of the cell. Applied to the image and to the placeholder alike. */
  boxClassName: string;
  /** Extra classes for the image itself, e.g. the object-fit rule. */
  imgClassName?: string;
  /** Extra classes for the placeholder box, e.g. a dashed border. */
  placeholderClassName?: string;
  /** Type scale for the placeholder label. */
  labelClassName?: string;
  onClick?: (event: MouseEvent<HTMLImageElement>) => void;
  title?: string;
  /** Fired when a source fails to load, so the caller can total failures up. */
  onFailed?: (src: string) => void;
}

/**
 * A preview thumbnail that tells the three "blank cell" cases apart.
 *
 * A large import leaves hundreds of cells empty for minutes while the browser
 * works through the queue, and an empty cell used to read the same whether the
 * extractor found no image, the URL failed, or the bytes were still in flight.
 * Only the first of those is an extraction problem.
 *
 * The image stays mounted and merely invisible until it resolves: it keeps its
 * box, so lazy loading still fires, and the placeholder overlays it rather than
 * displacing it, so the row height never shifts.
 */
export function ImageCell({
  src,
  alt,
  boxClassName,
  imgClassName,
  placeholderClassName,
  labelClassName = 'text-[10px]',
  onClick,
  title,
  onFailed,
}: ImageCellProps) {
  const [status, setStatus] = useState<Exclude<ImageCellStatus, 'not_extracted'>>('loading');
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    setStatus('loading');
    // A cached image can finish decoding before onLoad is attached, which would
    // otherwise strand the cell on the loading placeholder for good.
    const el = imgRef.current;
    if (el?.complete && el.naturalWidth > 0) setStatus('loaded');
  }, [src]);

  const placeholder = (state: Exclude<ImageCellStatus, 'loaded'>, label: string, tip: string) => (
    <div
      className={clsx(
        boxClassName,
        placeholderClassName,
        labelClassName,
        'flex items-center justify-center text-center leading-tight px-0.5',
        state === 'failed' && 'text-amber-600',
        state === 'not_extracted' && 'text-gray-400',
        state === 'loading' && 'animate-pulse bg-gray-100 text-gray-400',
        state !== 'not_extracted' && 'absolute inset-0',
      )}
      data-image-state={state}
      title={tip}
    >
      {label}
    </div>
  );

  if (!src) {
    return placeholder(
      'not_extracted',
      'Not extracted',
      'The extractor returned no image for this product',
    );
  }

  return (
    <div className={clsx(boxClassName, 'relative')}>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={clsx(boxClassName, imgClassName, status !== 'loaded' && 'invisible')}
        loading="lazy"
        onClick={onClick}
        title={title}
        onLoad={() => setStatus('loaded')}
        onError={() => {
          setStatus('failed');
          onFailed?.(src);
        }}
        data-testid="image-cell-img"
      />
      {status === 'loading' && placeholder('loading', 'Loading…', src)}
      {status === 'failed' && placeholder('failed', '⚠ Image failed', `Image failed to load: ${src}`)}
    </div>
  );
}
